import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Button, Card, CardBody, CardFooter, CardHeader, Col,
  Form, FormFeedback, FormGroup, FormText, Input, Label, Row,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import FlashesStore from 'src/stores/FlashesStore';
import {
  TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL,
} from 'src/util/constants';
import { convertEnergyPricePartnerToPowertracer, convertEnergyPricePowertracerToPartner } from 'src/util/conversions';
import { useIntlCurrency } from 'src/util/i18n/currency';

import { isFloatValue } from 'src/util/math';
import { isBuyPriceValid, isSellPriceValid } from 'src/util/tradeRule/validation';
import { getPriceHelpText, getPriceLabel } from './i18n/helpers';

import { buildClauseInput, getPrice } from './helpers';

const FlatPricingFormPropertyFragment = graphql`
  fragment FlatPricingForm_property on Property {
    id
    timezone
    publicHolidayRegion
  }
`;

const FlatPricingFormFormMeterFragment = graphql`
  fragment FlatPricingForm_meter on Meter {
    id
    identifier
    title
    tradePointId
    communityRules: rules(first: 500, type: TRADE_TYPE_COMMUNITY, state: TRADE_RULE_STATE_ACCEPTED) {
      edges {
        node {
          id
          priority
          tradeType
          state
          buyer {
            userId
            communityId
            residualId
            tradePoint {
              id
            }
          }
          seller {
            userId
            communityId
            residualId
            tradePoint {
              id
            }
          }
          clauses {
            edges {
              node {
                price
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * @param {object} props
 * @param {object} props.property Property data containing timezone and public holiday
 * region
 * @param {object} props.meter Meter data containing trade point ID and community rules
 * @param {object} props.router
 * @param {Function} props.onPricingButtonClick function handling the change of
 * state between the time of use pricing and flat pricing forms.
 * @param {Function} props.handleTradeRuleProposeCommunity  Mutation handler for
 * ProposeCommunityTradeMutation
 * @param {boolean} props.processing whether the mutation is being processed.
 * @returns {React.ReactComponentElement} - FlatPricingForm component
 */
function FlatPricingForm(props) {
  let { property, meter } = props;
  const {
    onPricingButtonClick, router, processing, handleTradeRuleProposeCommunity,
  } = props;

  if (!property || !meter) {
    return <Loading />;
  }

  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  property = useFragment(FlatPricingFormPropertyFragment, property);
  meter = useFragment(FlatPricingFormFormMeterFragment, meter);

  const timeOfUsePricingLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.flat_pricing_form.time_of_use_pricing.label',
    defaultMessage: 'Time-of-use pricing',
  });
  const priceInvalidFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.flat_pricing_form.form_feedback.invalid_price',
    defaultMessage: 'Invalid price',
  });
  const submitLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.flat_pricing_form.submit.label',
    defaultMessage: 'Set',
  });
  const cancelLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.flat_pricing_form.cancel.label',
    defaultMessage: 'Cancel',
  });

  const setCommunityTradeHeading = intl.formatMessage({ id: 'trade_rule.trade_rule_set_community.flat_pricing_form.heading.label', defaultMessage: 'Set community trade rules for {meter}' }, { meter: meter.title });

  const { tradePointId } = meter || {};

  const initialBuyPrice = getPrice(meter, TRADE_DIRECTION_BUY);
  const initialSellPrice = getPrice(meter, TRADE_DIRECTION_SELL);

  const [buyPrice, setBuyPrice] = useState(initialBuyPrice);
  const [sellPrice, setSellPrice] = useState(initialSellPrice);
  const [buyPriceValid, setBuyPriceValid] = useState(null);
  const [sellPriceValid, setSellPriceValid] = useState(null);

  const handleBuyPriceChange = (event) => {
    const { value: updatedPrice } = event.target || {};
    const updatedPriceBig = convertEnergyPricePartnerToPowertracer(
      intlCurrency,
      parseFloat(updatedPrice),
    );
    setBuyPrice(updatedPriceBig);
    setBuyPriceValid(isFloatValue(updatedPriceBig));
  };

  const handleSellPriceChange = (event) => {
    const { value: updatedPrice } = event.target || {};
    const updatedPriceBig = convertEnergyPricePartnerToPowertracer(
      intlCurrency,
      parseFloat(updatedPrice),
    );
    setSellPrice(updatedPriceBig);
    setSellPriceValid(isFloatValue(updatedPriceBig));
  };

  const mutationName = 'proposeCommunityTrade';

  const handleSubmit = (event) => {
    event.preventDefault();

    const { timezone, publicHolidayRegion } = property || {};

    const processingMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_set_community.flat_pricing_form.error.processing.message', defaultMessage: 'We are still processing your request...' });

    const errorMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_set_community.flat_pricing_form.error.invalid_data.message', defaultMessage: 'Form data not valid. Please see below.' });

    const errorMissingTradePointId = intl.formatMessage({ id: 'trade_rule.trade_rule_set_community.flat_pricing_form.response_message.error.missing_trade_point_id.message', defaultMessage: 'Trade point ID is required' });

    if (processing) {
      FlashesStore.flash(FlashesStore.INFO, processingMsg, mutationName);
      return;
    }

    if (!tradePointId) {
      FlashesStore.flash(FlashesStore.ERROR, errorMissingTradePointId, mutationName);
      return;
    }

    if (!isBuyPriceValid || !isSellPriceValid) {
      FlashesStore.flash(FlashesStore.ERROR, errorMsg, mutationName);
      return;
    }

    const input = {
      tradePointId,
      buyClauses: buildClauseInput(buyPrice, timezone, publicHolidayRegion),
      sellClauses: buildClauseInput(sellPrice, timezone, publicHolidayRegion),
    };

    handleTradeRuleProposeCommunity(input);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Card>
        <CardHeader className="d-flex flex-wrap">
          <h2 className="mb-0">
            {setCommunityTradeHeading}
          </h2>
          <Button color="darken" className="ms-auto" onClick={onPricingButtonClick}>
            {timeOfUsePricingLabel}
          </Button>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="buyPrice">{getPriceLabel(intl, TRADE_DIRECTION_BUY)}</Label>
                <Input type="number" name="buyPrice" id="buyPrice" value={convertEnergyPricePowertracerToPartner(intlCurrency, buyPrice)} onChange={handleBuyPriceChange} step="any" disabled={processing} valid={buyPriceValid} invalid={buyPriceValid !== null && !buyPriceValid} />
                <FormFeedback>{priceInvalidFeedback}</FormFeedback>
                <FormText>
                  {getPriceHelpText(intl, TRADE_DIRECTION_BUY)}
                </FormText>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="sellPrice">{getPriceLabel(intl, TRADE_DIRECTION_SELL)}</Label>
                <Input type="number" name="sellPrice" id="sellPrice" value={convertEnergyPricePowertracerToPartner(intlCurrency, sellPrice)} onChange={handleSellPriceChange} step="any" disabled={processing} valid={sellPriceValid} invalid={sellPriceValid !== null && !sellPriceValid} />
                <FormFeedback>{priceInvalidFeedback}</FormFeedback>
                <FormText>
                  {getPriceHelpText(intl, TRADE_DIRECTION_SELL)}
                </FormText>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
        <CardFooter>
          <Button color="primary" className="me-2" disabled={processing}>
            {submitLabel}
          </Button>
          <Button color="" onClick={() => (router.go(-1))} disabled={processing}>
            {cancelLabel}
          </Button>
        </CardFooter>
      </Card>
    </Form>
  );
}

FlatPricingForm.propTypes = {
  property: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  meter: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onPricingButtonClick: PropTypes.func.isRequired,
  processing: PropTypes.bool.isRequired,
  handleTradeRuleProposeCommunity: PropTypes.func.isRequired,
};

export default FlatPricingForm;
